import { Fragment, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { AlertTriangle, Check, CheckCircle2, Copy, HelpCircle, Info, XCircle } from "lucide-react";
import "./dialog.css";

/**
 * Diálogos y toasts propios, con API imperativa (se llaman desde cualquier lado, no
 * hace falta contexto de React). El host se monta solo, una vez, en un root aparte
 * colgado de <body>. Los diálogos se muestran de uno en uno (cola); los toasts se apilan.
 *
 * Tema: claro con el acento de marca por defecto; si al abrir hay un `.sa-root`
 * (superadmin) se usa el tema oscuro. Se puede forzar con `theme`.
 *
 * openDialog({ icon, title, text, html, details, note, noteTone, pre, copyText,
 *              confirmText, cancelText, showCancel, danger, timer, theme })
 *   → Promise<{ isConfirmed, isDismissed }>
 * pushToast({ icon, title, text, timer, theme }) → Promise<void> (al cerrarse)
 */

const ICONS = { success: CheckCircle2, warning: AlertTriangle, error: XCircle, question: HelpCircle, info: Info };
const DIALOG_EXIT_MS = 240;
const TOAST_EXIT_MS = 220;

let state = { dialogs: [], toasts: [] };
const listeners = new Set();
let seq = 0;
let host = null;

const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const getState = () => state;
const setState = (patch) => { state = { ...state, ...patch }; emit(); };

function ensureHost() {
  if (host || typeof document === "undefined") return;
  const el = document.createElement("div");
  el.id = "tl-dialog-host";
  document.body.appendChild(el);
  host = createRoot(el);
  host.render(<DialogHost />);
}

const currentTheme = () => (document.querySelector(".sa-root") ? "dark" : "light");

export function openDialog(config = {}) {
  ensureHost();
  return new Promise((resolve) => {
    setState({ dialogs: [...state.dialogs, { ...config, id: ++seq, theme: config.theme || currentTheme(), resolve }] });
  });
}

export function pushToast(config = {}) {
  ensureHost();
  return new Promise((resolve) => {
    setState({ toasts: [...state.toasts, { ...config, id: ++seq, theme: config.theme || currentTheme(), resolve }] });
  });
}

function removeDialog(id, result) {
  const d = state.dialogs.find((x) => x.id === id);
  if (!d) return;
  setState({ dialogs: state.dialogs.filter((x) => x.id !== id) });
  d.resolve(result);
}

function removeToast(id) {
  const t = state.toasts.find((x) => x.id === id);
  if (!t) return;
  setState({ toasts: state.toasts.filter((x) => x.id !== id) });
  t.resolve();
}

function DialogHost() {
  const s = useSyncExternalStore(subscribe, getState);
  const current = s.dialogs[0];
  return (
    <>
      {current && <DialogView key={current.id} dialog={current} />}
      <ToastStack toasts={s.toasts} />
    </>
  );
}

const normalizeDetails = (details) => {
  if (!Array.isArray(details)) return [];
  return details
    .map((d) => (Array.isArray(d) ? { label: d[0], value: d[1] } : d))
    .filter((d) => d && d.label != null && d.value != null && d.value !== "");
};

function DialogView({ dialog }) {
  const {
    id, icon = "info", title, text, html, details, note, noteTone, pre, copyText,
    confirmText = "Aceptar", cancelText = "Cancelar", showCancel = false, danger = false, timer, theme,
  } = dialog;
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const closing = useRef(false);

  const close = useCallback((result) => {
    if (closing.current) return;
    closing.current = true;
    setShown(false);
    setTimeout(() => removeDialog(id, result), DIALOG_EXIT_MS);
  }, [id]);
  const confirm = useCallback(() => close({ isConfirmed: true, isDismissed: false }), [close]);
  const dismiss = useCallback(() => close({ isConfirmed: false, isDismissed: true }), [close]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
    const prevFocus = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const card = cardRef.current;
    // En acciones destructivas el foco arranca en "cancelar": Enter no borra nada por accidente.
    card?.querySelector(danger && showCancel ? "[data-cancel]" : "[data-confirm]")?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); dismiss(); return; }
      if (e.key === "Tab" && card) {
        const items = Array.from(card.querySelectorAll("button:not([disabled])"));
        if (!items.length) return;
        const i = items.indexOf(document.activeElement);
        const next = e.shiftKey ? (i <= 0 ? items.length - 1 : i - 1) : (i === -1 || i === items.length - 1 ? 0 : i + 1);
        e.preventDefault();
        items[next].focus();
      }
    };
    // capture: gana al Escape del sheet que pueda estar debajo.
    window.addEventListener("keydown", onKey, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
      if (prevFocus && typeof prevFocus.focus === "function" && document.contains(prevFocus)) prevFocus.focus({ preventScroll: true });
    };
  }, [danger, showCancel, dismiss]);

  useEffect(() => {
    if (!timer) return undefined;
    const t = setTimeout(dismiss, timer);
    return () => clearTimeout(t);
  }, [timer, dismiss]);

  const copy = () => {
    navigator.clipboard?.writeText(copyText).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const Icon = ICONS[icon] || Info;
  const rows = normalizeDetails(details);
  const hasBody = !!(text || html || rows.length || note || pre);
  const tone = noteTone || (danger ? "danger" : "");

  return (
    <div className="dl-layer" data-state={shown ? "open" : "closed"} data-theme={theme}>
      <div className="dl-scrim" onClick={dismiss} />
      <div
        ref={cardRef}
        className="dl-card"
        role={showCancel ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={`dl-title-${id}`}
        aria-describedby={text ? `dl-text-${id}` : undefined}
      >
        <div className="dl-head">
          <span className={`dl-icon is-${icon}`} aria-hidden><Icon size={22} strokeWidth={2.2} /></span>
          <h2 className="dl-title" id={`dl-title-${id}`}>{title}</h2>
        </div>
        {hasBody && (
          <div className="dl-body">
            {text && <p className="dl-text" id={`dl-text-${id}`}>{text}</p>}
            {html && <div className="dl-html" dangerouslySetInnerHTML={{ __html: html }} />}
            {rows.length > 0 && (
              <dl className="dl-kv">
                {rows.map((r, i) => (
                  <Fragment key={i}>
                    <dt>{r.label}</dt>
                    <dd className={r.tone ? `is-${r.tone}` : undefined}>{r.value}</dd>
                  </Fragment>
                ))}
              </dl>
            )}
            {note && <p className={`dl-note ${tone ? `is-${tone}` : ""}`}>{note}</p>}
            {pre && (
              <div className="dl-pre">
                <div className="dl-pre-head">
                  <span>Detalle para soporte</span>
                  {copyText && (
                    <button type="button" className={`dl-copy ${copied ? "is-done" : ""}`} onClick={copy}>
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copiado" : "Copiar"}
                    </button>
                  )}
                </div>
                <pre>{pre}</pre>
              </div>
            )}
          </div>
        )}
        <div className={`dl-actions ${showCancel ? "" : "is-single"}`}>
          {showCancel && <button type="button" className="dl-btn" data-cancel onClick={dismiss}>{cancelText}</button>}
          <button type="button" className={`dl-btn ${danger ? "dl-btn-danger" : "dl-btn-primary"}`} data-confirm onClick={confirm}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div className="dl-toasts" data-theme={toasts[0].theme}>
      {toasts.map((t) => <ToastView key={t.id} toast={t} />)}
    </div>
  );
}

function ToastView({ toast }) {
  const { id, icon = "success", title, text, timer = 3000 } = toast;
  const [shown, setShown] = useState(false);
  const [dragging, setDragging] = useState(false);
  const closing = useRef(false);
  const drag = useRef(null);
  const elRef = useRef(null);

  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    setShown(false);
    setTimeout(() => removeToast(id), TOAST_EXIT_MS);
  }, [id]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
    const t = timer ? setTimeout(close, timer) : null;
    return () => { cancelAnimationFrame(raf); if (t) clearTimeout(t); };
  }, [timer, close]);

  // Deslizar hacia arriba para quitarlo (como las notificaciones del sistema).
  const onPointerDown = (e) => {
    if (e.pointerType === "mouse") return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startY: e.clientY, dy: 0 };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || !elRef.current) return;
    d.dy = e.clientY - d.startY;
    const y = d.dy < 0 ? d.dy : (d.dy * 0.2) / (1 + d.dy / 80);
    elRef.current.style.transform = `translate3d(0, ${y}px, 0)`;
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (!elRef.current) return;
    elRef.current.style.transform = "";
    if (d && d.dy < -20) close();
  };

  const Icon = ICONS[icon] || Info;
  return (
    <div
      ref={elRef}
      className={`dl-toast ${dragging ? "is-dragging" : ""}`}
      role="status"
      data-state={shown ? "open" : "closed"}
      onClick={close}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <span className={`dl-toast-icon is-${icon}`} aria-hidden><Icon size={20} strokeWidth={2.2} /></span>
      <div className="dl-toast-text">
        {title && <div className="dl-toast-title">{title}</div>}
        {text && <div className="dl-toast-sub">{text}</div>}
      </div>
    </div>
  );
}
